<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {Tooltip} from 'bootstrap';
import DriverLicenseSidesUpload from './DriverLicenseSidesUpload.vue';
import FilePickerControls from './FilePickerControls.vue';
import {preparePickedFile, IMAGE_OR_PDF_TYPES} from '../fileUploadValidation';

const model = defineModel({
  type: Object,
  default: () => ({})
});

const insuranceFileName = ref('');
const insurancePreparing = ref(false);

const hasExistingInsurance = computed(() => model.value.hasInsuranceDocument && !model.value.insuranceDocFile);

const hasInsuranceOnFile = computed(() => !!insuranceFileName.value || hasExistingInsurance.value);

const licenseExpiryError = ref('');
const insuranceExpiryError = ref('');

/**
 * Accepts a picked insurance document, downscaling it first when it is a photo.
 *
 * @param {string} type the document being picked; only 'insurance' is handled here
 * @param {Event} e the change event from the file input
 */
async function onSelect(type, e) {
  const files = e?.target?.files;
  if (!files || files.length === 0 || type !== 'insurance') return;

  const input = e.target;
  insurancePreparing.value = true;
  try {
    const file = await preparePickedFile(files[0], e, IMAGE_OR_PDF_TYPES,
        {subject: 'insurance certificate'});
    if (!file) return;

    insuranceFileName.value = file.name;
    model.value.insuranceDocFile = file;
    model.value.insuranceDocId = model.value.insuranceDocId || 'PENDING_UPLOAD';
    model.value.hasInsuranceDocument = true;
  } finally {
    insurancePreparing.value = false;
    // Cleared unconditionally so re-picking the same file still raises a change event.
    input.value = '';
  }
}

function removeInsuranceFile() {
  insuranceFileName.value = '';
  model.value.insuranceDocFile = null;
  model.value.hasInsuranceDocument = false;
  model.value.insuranceDocId = null;
}

const rootEl = ref(null);
const tooltipInstances = [];

// Only show the declaration error after the user has interacted with
// the checkboxes or attempted to advance the wizard step.
const declarationTouched = ref(false);

const declarationError = computed(() => {
  if (!declarationTouched.value) return '';
  const allChecked =
    model.value.declaresNoExcessiveCancellations &&
    model.value.declaresNoExcessiveAtFaultClaims &&
    model.value.declaresNoExcessiveTotalClaims;
  return allChecked ? '' : 'You must confirm all insurance eligibility declarations to continue.';
});

function onDeclarationChange() {
  declarationTouched.value = true;
}

// Called by the hosting wizard when Continue is refused, so a driver who scrolled past the
// declarations sees the alert rather than only a toast pointing at a section that looks fine.
defineExpose({
  revealDeclarationError() {
    declarationTouched.value = true;
  }
});

// Bootstrap is consumed here as an ES import, never assigned to window, so the previous
// `typeof bootstrap === 'undefined'` guard was always true: no tooltip was ever constructed and the
// browser fell back to the native `title` bubble — delayed, tiny, and invisible on touch.
onMounted(() => {
  if (!rootEl.value) return;
  rootEl.value.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
    tooltipInstances.push(new Tooltip(el, {
      // Appended to <body> rather than beside the icon, which would place it inside the <label> and
      // let the card's overflow clip it.
      container: 'body',
      customClass: 'declaration-tooltip',
      trigger: 'hover focus',
    }));
  });
});

onUnmounted(() => {
  tooltipInstances.forEach(t => t.dispose());
  tooltipInstances.length = 0;
});

function validateExpiryDate(type) {
  const today = new Date().toISOString().split('T')[0];

  if (type === 'license') {
    if (!model.value.licenseExpiryDate) {
      licenseExpiryError.value = 'License expiry date is required.';
    } else if (model.value.licenseExpiryDate <= today) {
      licenseExpiryError.value = 'License expiry date must be in the future.';
    } else {
      licenseExpiryError.value = '';
    }
  } else if (type === 'insurance') {
    if (!model.value.insuranceExpiryDate) {
      insuranceExpiryError.value = 'Insurance expiry date is required.';
    } else if (model.value.insuranceExpiryDate <= today) {
      insuranceExpiryError.value = 'Insurance expiry date must be in the future.';
    } else {
      insuranceExpiryError.value = '';
    }
  }
}
</script>

<template>
  <div class="driver-onboarding-step driver-onboarding-step-documents" ref="rootEl">
    <div class="row g-4">
      <div class="col-md-6">
        <h5 class="mb-3">Driver's License</h5>

        <DriverLicenseSidesUpload v-model="model"/>

        <div class="mb-3">
          <label class="form-label f-w-600" for="license-expiry-input">Expiration Date <span
              class="text-danger">*</span></label>
          <input
              id="license-expiry-input"
              v-model="model.licenseExpiryDate"
              class="form-control"
              :class="{ 'is-invalid': licenseExpiryError }"
              type="date"
              @blur="validateExpiryDate('license')"
              required
          >
          <div v-if="licenseExpiryError" class="invalid-feedback d-block">
            {{ licenseExpiryError }}
          </div>
          <small class="form-text text-muted d-flex gap-2 mt-2">
            <i class="fa-solid fa-calendar-days text-primary mt-1"></i>
            <span>We'll remind you 30 days before your license expires so you can renew without interruption.</span>
          </small>
        </div>
      </div>

      <div class="col-md-6">
        <h5 class="mb-3">Insurance Certificate (Pink Slip)</h5>

        <div class="mb-3">
          <div class="form-label f-w-600">
            Document Upload {{ hasExistingInsurance ? '(Re-upload to replace)' : '' }}
          </div>
          <!-- An accept list containing application/pdf is enough to make Android Chrome offer
               Camera as well as Files, so this was a native input until the licence sides above
               grew their own two-button picker: on a phone the native control renders as a cramped
               "Choose File / No file chosen" strip beside two full-width buttons, and reads as a
               different, lesser thing. Both paths still exist — the camera input carries `capture`
               and returns a photo, the browse input keeps the PDF accept. -->
          <FilePickerControls input-id="insurance-input" accept="image/*,application/pdf"
                              :disabled="insurancePreparing"
                              :camera-label="hasInsuranceOnFile ? 'Retake' : 'Take photo'"
                              :file-label="hasInsuranceOnFile ? 'Replace' : 'Choose file'"
                              :sole-label="hasInsuranceOnFile ? 'Replace document' : 'Choose file'"
                              @picked="onSelect('insurance', $event)"/>
          <small v-if="insurancePreparing" class="text-muted d-block mt-1">
            <span class="spinner-border spinner-border-sm me-1"></span>Preparing document...
          </small>
          <div v-if="insuranceFileName" class="mt-2">
            <small class="text-success d-block mb-1">
              <i class="fa fa-check-circle"></i> File selected:
            </small>
            <div class="small text-muted d-flex justify-content-between align-items-center gap-2">
              <span class="text-truncate" :title="insuranceFileName">
                <i class="fa fa-file-alt me-1"></i>{{ insuranceFileName }}
              </span>
              <button type="button" class="btn btn-link btn-sm text-danger p-0 flex-shrink-0"
                      @click="removeInsuranceFile" title="Remove">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
          <small v-else-if="hasExistingInsurance" class="text-info d-block mt-1">
            <i class="fa fa-file-alt"></i> Document already uploaded. Select a new file to replace it.
          </small>
          <small v-else class="text-muted d-block mt-1">
            Upload your insurance certificate showing a valid coverage (max 5MB).
          </small>
        </div>

        <div class="mb-3">
          <label class="form-label f-w-600" for="insurance-expiry-input">Expiration Date <span
              class="text-danger">*</span></label>
          <input
              id="insurance-expiry-input"
              v-model="model.insuranceExpiryDate"
              class="form-control"
              :class="{ 'is-invalid': insuranceExpiryError }"
              type="date"
              @blur="validateExpiryDate('insurance')"
              required
          >
          <div v-if="insuranceExpiryError" class="invalid-feedback d-block">
            {{ insuranceExpiryError }}
          </div>
          <small class="form-text text-muted d-flex gap-2 mt-2">
            <i class="fa-solid fa-calendar-days text-primary mt-1"></i>
            <span>We'll remind you 30 days before your insurance expires so you can renew without interruption.</span>
          </small>
        </div>
      </div>
    </div>

    <div class="alert alert-info mt-3" role="alert">
      <i class="fa fa-info-circle"></i>
      <strong>Note:</strong> All documents and expiration dates must be provided before proceeding. Files will be
      securely uploaded when you complete the registration process.
    </div>

    <div class="card border mt-4">
      <div class="card-header bg-light-dark py-2">
        <h6 class="mb-0 f-w-600">
          <i class="fa-solid fa-shield-halved text-primary me-2"></i>Insurance Eligibility Declaration
        </h6>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Please confirm that each of the following
          statements applies to you. <strong>This declaration must be re-confirmed annually.</strong>
        </p>

        <div class="mb-3 d-flex align-items-start gap-2">
          <input
              id="decl-cancellations"
              class="form-check-input mt-1 flex-shrink-0"
              type="checkbox"
              v-model="model.declaresNoExcessiveCancellations"
              @change="onDeclarationChange"
          >
          <label class="form-check-label small" for="decl-cancellations">
            I have had <strong>no more than 1 cancellation for non-payment</strong> in the past 3 years.
            <span
                class="declaration-help text-muted ms-1"
                tabindex="0"
                role="button"
                aria-label="What this means"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="A cancellation for non-payment occurs when an insurance policy is cancelled because the premium was not paid. More than 1 in 3 years may affect your insurability."
                @click.prevent.stop
            ><i class="fa-solid fa-circle-question" aria-hidden="true"></i></span>
          </label>
        </div>

        <div class="mb-3 d-flex align-items-start gap-2">
          <input
              id="decl-at-fault"
              class="form-check-input mt-1 flex-shrink-0"
              type="checkbox"
              v-model="model.declaresNoExcessiveAtFaultClaims"
              @change="onDeclarationChange"
          >
          <label class="form-check-label small" for="decl-at-fault">
            I have had <strong>no more than 1 at-fault (AF) claim</strong> in the past 3 years.
            <span
                class="declaration-help text-muted ms-1"
                tabindex="0"
                role="button"
                aria-label="What this means"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="An at-fault claim is one where you were found to be responsible for the accident. We require that our drivers have no more than 1 at-fault claim in the past 3 years to qualify for coverage."
                @click.prevent.stop
            ><i class="fa-solid fa-circle-question" aria-hidden="true"></i></span>
          </label>
        </div>

        <div class="mb-3 d-flex align-items-start gap-2">
          <input
              id="decl-total-claims"
              class="form-check-input mt-1 flex-shrink-0"
              type="checkbox"
              v-model="model.declaresNoExcessiveTotalClaims"
              @change="onDeclarationChange"
          >
          <label class="form-check-label small" for="decl-total-claims">
            I have had <strong>no more than 3 claims in total</strong> in the past 6 years.
            <span
                class="declaration-help text-muted ms-1"
                tabindex="0"
                role="button"
                aria-label="What this means"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="This includes all claims regardless of fault (at-fault, not-at-fault, comprehensive, etc.). We require that our drivers have no more than 3 total claims in the past 6 years to qualify for rideshare coverage."
                @click.prevent.stop
            ><i class="fa-solid fa-circle-question" aria-hidden="true"></i></span>
          </label>
        </div>

        <div v-if="declarationError" class="alert alert-danger py-2 small mt-2">
          <i class="fa-solid fa-circle-exclamation me-1"></i>{{ declarationError }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Focusable so the explanation is reachable by keyboard, and by tap on a device with no hover. */
.declaration-help {
  cursor: pointer;
}

.declaration-help:focus-visible {
  outline: 2px solid var(--theme-default, #7366ff);
  outline-offset: 2px;
  border-radius: 50%;
}
</style>

<style>
/* Rendered on <body>, outside this component's scope, so these cannot be scoped. The theme's default
   is 200px wide, centred and translucent, which turns a two-sentence explanation into a tall washed-out
   column — the reason the hover was unreadable. */
.declaration-tooltip .tooltip-inner {
  max-width: 320px;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  text-align: left;
}

.declaration-tooltip.tooltip.show {
  --bs-tooltip-opacity: 1;
  opacity: 1;
}
</style>
